import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { GraduationCap, BookOpen, School, Users, Briefcase } from 'lucide-react';

const LandingPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 flex items-center justify-center p-4">
      <div className="max-w-5xl w-full">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-6xl font-bold text-white mb-4 drop-shadow-lg">
            🎓 MyClassAI
          </h1>
          <p className="text-2xl text-white/90 mb-2">
            AI-Powered Learning + School Admin for LKG to Class 10
          </p>
          <p className="text-lg text-white/80">
            Audio Learning • SCERT & NCERT Aligned • Performance Analytics
          </p>
        </div>

        {/* Login Options */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Teacher Login Card */}
          <Card 
            className="cursor-pointer hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 bg-white/95 backdrop-blur"
            onClick={() => navigate('/teacher-login')}
          >
            <CardHeader className="text-center">
              <div className="flex justify-center mb-4">
                <div className="w-24 h-24 bg-blue-100 rounded-full flex items-center justify-center">
                  <GraduationCap className="w-16 h-16 text-blue-600" />
                </div>
              </div>
              <CardTitle className="text-3xl text-blue-900">Teacher Portal</CardTitle>
              <CardDescription className="text-lg">
                Manage syllabus, create tests, and track student performance
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-gray-700 mb-6">
                <li className="flex items-center">
                  <span className="text-green-500 mr-2">✓</span>
                  Mark topics as covered
                </li>
                <li className="flex items-center">
                  <span className="text-green-500 mr-2">✓</span>
                  Create term-based tests
                </li>
                <li className="flex items-center">
                  <span className="text-green-500 mr-2">✓</span>
                  View color-coded results
                </li>
                <li className="flex items-center">
                  <span className="text-green-500 mr-2">✓</span>
                  Detailed student analytics
                </li>
              </ul>
              <Button className="w-full py-6 text-lg bg-blue-600 hover:bg-blue-700" data-testid="teacher-login-card">
                Login as Teacher →
              </Button>
            </CardContent>
          </Card>

          {/* Student Login Card */}
          <Card 
            className="cursor-pointer hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 bg-white/95 backdrop-blur"
            onClick={() => navigate('/student-login')}
          >
            <CardHeader className="text-center">
              <div className="flex justify-center mb-4">
                <div className="w-24 h-24 bg-purple-100 rounded-full flex items-center justify-center">
                  <BookOpen className="w-16 h-16 text-purple-600" />
                </div>
              </div>
              <CardTitle className="text-3xl text-purple-900">Student Portal</CardTitle>
              <CardDescription className="text-lg">
                Take tests, learn with audio, and track your progress
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-gray-700 mb-6">
                <li className="flex items-center">
                  <span className="text-green-500 mr-2">✓</span>
                  Audio learning (English voices)
                </li>
                <li className="flex items-center">
                  <span className="text-green-500 mr-2">✓</span>
                  Take SCERT/NCERT-aligned tests
                </li>
                <li className="flex items-center">
                  <span className="text-green-500 mr-2">✓</span>
                  View detailed results
                </li>
                <li className="flex items-center">
                  <span className="text-green-500 mr-2">✓</span>
                  Track your performance
                </li>
              </ul>
              <Button className="w-full py-6 text-lg bg-purple-600 hover:bg-purple-700" data-testid="student-login-card">
                Login as Student →
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Head Teacher Card - More Visible */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl mx-auto">
          <Card
            className="cursor-pointer hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 bg-white/90 backdrop-blur"
            onClick={() => navigate('/head-teacher-login')}
          >
            <CardContent className="p-6 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                  <School className="w-7 h-7 text-purple-700" />
                </div>
                <div>
                  <h3 className="font-bold text-base text-purple-900">Principal / Head Teacher</h3>
                  <p className="text-xs text-gray-500">Teachers · Analytics · Exams</p>
                </div>
              </div>
              <Button size="sm" className="bg-purple-600 hover:bg-purple-700" data-testid="head-teacher-login-card">
                Login →
              </Button>
            </CardContent>
          </Card>

          <Card
            className="cursor-pointer hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 bg-white/90 backdrop-blur"
            onClick={() => navigate('/admin/login')}
          >
            <CardContent className="p-6 flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                  <Briefcase className="w-7 h-7 text-amber-700" />
                </div>
                <div>
                  <h3 className="font-bold text-base text-amber-900">Office Admin</h3>
                  <p className="text-xs text-gray-500">Admissions · Fees · PINs</p>
                </div>
              </div>
              <Button size="sm" className="bg-amber-600 hover:bg-amber-700" data-testid="admin-login-card">
                Login →
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Parent Portal Link */}
        <div className="mt-4 text-center">
          <button 
            onClick={() => navigate('/parent-login')}
            className="inline-flex items-center gap-2 text-white/80 hover:text-white transition-colors text-sm"
            data-testid="parent-login-link"
          >
            <Users size={16} />
            Parent Portal
          </button>
        </div>

        {/* Features Section */}
        <div className="mt-8 text-center">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white/20 backdrop-blur rounded-lg p-4">
              <h3 className="text-white font-bold text-lg mb-2">🎧 Audio Learning</h3>
              <p className="text-white/80 text-sm">Listen with multiple English voice options and adjustable pace</p>
            </div>
            <div className="bg-white/20 backdrop-blur rounded-lg p-4">
              <h3 className="text-white font-bold text-lg mb-2">SCERT & NCERT Aligned</h3>
              <p className="text-white/80 text-sm">100% aligned with official SCERT & NCERT syllabus</p>
            </div>
            <div className="bg-white/20 backdrop-blur rounded-lg p-4">
              <h3 className="text-white font-bold text-lg mb-2">📊 Performance Tracking</h3>
              <p className="text-white/80 text-sm">Detailed analytics and progress reports</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;
